import { BlueYonderZod } from "../../return";

interface ReturnWithBlueYonder {
  blueYonder?: BlueYonderZod | null;
}

/**
 * Checks if a return has a valid BlueYonder label.
 * A return is considered to have a valid BlueYonder label if:
 * 1. BlueYonder was chosen as the return method
 * 2. There is at least one BlueYonder return created
 * 3. The first BlueYonder return has a labelValue
 */
export function hasBlueYonderLabel(return_: ReturnWithBlueYonder): boolean {
  return (
    !!return_.blueYonder?.chosen &&
    return_.blueYonder.returns.length > 0 &&
    !!return_.blueYonder.returns[0]?.labelValue
  );
}

/**
 * Checks if a return is a BlueYonder return (BlueYonder was chosen).
 * This is a simpler check that doesn't verify if a label exists.
 */
export function isBlueYonderChosen(return_: ReturnWithBlueYonder): boolean {
  return !!return_.blueYonder?.chosen;
}

/**
 * Gets the BlueYonder label value from a return, if it exists.
 * Returns undefined if BlueYonder wasn't chosen or no label exists.
 */
export function getBlueYonderLabelValue(
  return_: ReturnWithBlueYonder,
): string | undefined {
  if (!return_.blueYonder?.chosen) {
    return undefined;
  }

  if (return_.blueYonder.returns.length === 0) {
    return undefined;
  }

  return return_.blueYonder.returns[0]?.labelValue || undefined;
}

/**
 * Checks if a return has a valid BlueYonder QR code (for boxless returns).
 * A return is considered to have a valid BlueYonder QR code if:
 * 1. BlueYonder was chosen as the return method
 * 2. There is at least one BlueYonder return created
 * 3. The first BlueYonder return has a qrCodeUri
 */
export function hasBlueYonderQrCode(return_: ReturnWithBlueYonder): boolean {
  return (
    !!return_.blueYonder?.chosen &&
    return_.blueYonder.returns.length > 0 &&
    !!return_.blueYonder.returns[0]?.qrCodeUri
  );
}

/**
 * Gets the BlueYonder QR code URI from a return, if it exists.
 * Returns undefined if BlueYonder wasn't chosen or no QR code exists.
 */
export function getBlueYonderQrCodeUri(
  return_: ReturnWithBlueYonder,
): string | undefined {
  if (!return_.blueYonder?.chosen) {
    return undefined;
  }

  if (return_.blueYonder.returns.length === 0) {
    return undefined;
  }

  return return_.blueYonder.returns[0]?.qrCodeUri || undefined;
}
